import {SubmissionErrors} from 'final-form';
import {useDispatch} from 'react-redux';
import {useNavigate} from 'react-router-dom';
import Row from '@frontend/ui-kit/Components/Row';
import Column from '@frontend/ui-kit/Components/Column';
import ContentSection from '@frontend/ui-kit/Components/ContentSection';
import Heading, {HEADING_TYPES} from '@frontend/ui-kit/Components/Heading';
import Text from '@frontend/ui-kit/Components/Text';
import Button, {BUTTON_TYPES} from '@frontend/ui-kit/Components/Button';
import Input from '@frontend/ui-kit/Components/Input';
import GlobalErrorMessage from '@frontend/ui-kit/Components/GlobalErrorMessage';
import {Form, Field} from '../../shared/FormComponents';
import {requestUserState, requestSignUp} from '../../../actions/signUp';
import analyticsService, {ANALYTICS_EVENTS} from '../../../analyticsService';
import {formatDate, getEqual, parseISODate, validateRequired} from '../../../utils';
import {AppDispatch} from '../../../store';
import {gettext} from '../../../translationService';
import {FORM_GLOBAL_ERROR, ROUTES, USER_STATUSES} from '../../../constants';
import {CheckStatusResponseData} from '../../../apiServices/types';
import {FormValues, UserStatus} from './types';
import './index.scss';

const USER_NOT_FOUND_REASONS = ['invalid_form', 'user_not_found'];

/* istanbul ignore next */
const validate = (values: FormValues) => {
    return {
        login: validateRequired(values.login)
    };
};

const getFutureInviteErrorMessage = (invitationDate?: string): string => gettext(
    'Account available on {invitationDate}. If you think this is a mistake, please contact your human resources department.',
    {invitationDate: formatDate(parseISODate(invitationDate), 'MM/dd/yyyy')}
);

const ERROR_MESSAGE_GETTER_BY_STATUS: Partial<Record<UserStatus, (invitationDate?: string) => string>> = {
    [USER_STATUSES.deactivated]: () => gettext('Account deactivated. If you think this is a mistake, please contact your human resources department.'),
    [USER_STATUSES.temporaryLocked]: () => gettext('Due to multiple invalid login attempts, your account has been locked. Please wait 30 minutes before trying to access your account.'),
    [USER_STATUSES.futureInvite]: getFutureInviteErrorMessage
};

const getUserStateErrorMessage = ({status, reason, invitation_date: invitationDate}: CheckStatusResponseData): string | undefined => {
    if (USER_NOT_FOUND_REASONS.some(getEqual(reason))) {
        return gettext('We couldn’t verify this email or phone number. Please check it and try again or contact your human resources department.');
    }

    return status && ERROR_MESSAGE_GETTER_BY_STATUS[status]?.(invitationDate);
};

const ActivationForm = (): JSX.Element => {
    const dispatch = useDispatch<AppDispatch>();
    const navigate = useNavigate();

    const formData: FormValues = {login: ''};
    const genericErrorMessage = gettext('Something went wrong. It’s not you, it’s us. Please try again later.');

    const onSubmit = async ({login}: FormValues): Promise<SubmissionErrors | void> => {
        const {userState, isSuccess, statusCode} = await dispatch(requestUserState(login));
        const {status, reason} = userState ?? {};
        const isActivationAvailable = [USER_STATUSES.active, USER_STATUSES.notActive].some(getEqual(status));

        analyticsService.track(ANALYTICS_EVENTS.membership.activationSubmitted, {status, reason, ...(!isSuccess && {status_code: statusCode})});

        if (!isActivationAvailable) {
            const userStateErrorMessage = getUserStateErrorMessage(userState ?? {}) ?? genericErrorMessage;

            return {[FORM_GLOBAL_ERROR]: userStateErrorMessage};
        }

        const {signUpInfo} = await dispatch(requestSignUp(login));

        if (!signUpInfo?.success) {
            return {[FORM_GLOBAL_ERROR]: genericErrorMessage};
        }

        navigate(ROUTES.activated);
    };

    return (
        <ContentSection className='membership-activation-form'>
            <Heading type={HEADING_TYPES['1']} className='mb-12'>
                {gettext('Haven’t Activated Your HealthJoy Account')}
            </Heading>

            <Text className='mb-12'>
                {gettext(`
                    HealthJoy is the first stop for your healthcare and benefits needs.
                    You’ll have access to online medical professionals, healthcare experts and all your employee benefits.
                    Activate your company HealthJoy account by entering your email address below, and we’ll resend you your activation email.`)}
            </Text>

            <Form<FormValues> initialValues={formData} onSubmit={onSubmit} validate={validate}>
                {({handleSubmit, submitError, dirtySinceLastSubmit}) => {
                    return (
                        <form onSubmit={handleSubmit} noValidate>
                            <div className='membership-activation-form-body'>
                                <Row top='xs'>
                                    <Column xs={12} md={6}>
                                        <Field wrapperClassName='membership-activation-form-body__login-input' name='login'>
                                            {(props: any) => <Input {...props} data-testid='login' label={gettext('Receive activation email again')} placeholder={gettext('Enter your company email or phone number')}/>}
                                        </Field>
                                    </Column>

                                    <Column xs={12} md={6}>
                                        <Button type={BUTTON_TYPES.primary} className='membership-activation-form-body__submit-button' isSubmit>
                                            {gettext('Submit')}
                                        </Button>
                                    </Column>
                                </Row>

                                {submitError && !dirtySinceLastSubmit && (
                                    <GlobalErrorMessage>
                                        {submitError}
                                    </GlobalErrorMessage>
                                )}
                            </div>
                        </form>
                    );
                }}
            </Form>
        </ContentSection>
    );
};

export {ActivationForm as TestableActivationForm};
export default ActivationForm;
